import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { TopUpLink } from "@/components/CreditsNotice";
import { uploadFileSmart } from "@/lib/chunkedUpload";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  X,
  Sparkles,
  Loader2,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { errorDetail } from "@/lib/utils";

// Friendly labels for every stage the backend reports while extracting.
// The user can see exactly where their PDF is in the pipeline.
// NOTE (Feb 2026): The pipeline now marks status=completed AFTER ai_suggestions/saving.
// Page snapshots + Perplexity research run in PARALLEL in the background — they're shown
// as a separate "enriching" pill on the edit page, not as blocking stages here.
const STAGE_STEPS = [
  { key: "queued", label: "Queued for AI" },
  { key: "reading_files", label: "Reading your PDF" },
  { key: "ai_extracting", label: "AI is extracting product details" },
  { key: "ai_suggestions", label: "Generating AI suggestions" },
  { key: "saving_results", label: "Saving results" },
];

export default function AdminProductUpload() {
  const [docs, setDocs] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // null | 0..100 while a chunked upload is running
  const [creating, setCreating] = useState(false);
  // While the AI runs we hold the in-flight product here and poll it.
  const [pending, setPending] = useState(null); // {id, extraction_status, extraction_stage, extraction_error, name}
  const nav = useNavigate();

  const uploadFiles = async (filelist, kind) => {
    if (!filelist?.length) return;
    setUploading(true);
    setUploadPct(null);
    try {
      const uploaded = [];
      for (const f of filelist) {
        // Auto-routes through chunked upload for files bigger than ~6 MB so
        // 100 MB brochures make it past Cloudflare's body-size limit.
        const data = await uploadFileSmart(f, {
          kind,
          onProgress: ({ bytes, size }) => {
            if (size > 0) setUploadPct(Math.round((bytes / size) * 100));
          },
        });
        uploaded.push(data);
      }
      if (kind === "image") setImages((x) => [...x, ...uploaded]);
      else setDocs((x) => [...x, ...uploaded]);
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      setUploadPct(null);
    }
  };

  const submit = async () => {
    if (!docs.length && !images.length) {
      alert("Please upload at least one document or image first.");
      return;
    }
    setCreating(true);
    try {
      const { data: product } = await api.post("/products", {
        file_ids: docs.map((f) => f.id),
        image_ids: images.map((f) => f.id),
      });
      try {
        // Returns immediately — extraction runs in background.
        await api.post(`/products/${product.id}/extract`);
      } catch (e) {
        console.error("Extraction kickoff error:", e);
      }
      // Switch the page to the live-progress view. We DO NOT navigate away.
      setPending({
        id: product.id,
        extraction_status: "processing",
        extraction_stage: "queued",
        extraction_error: null,
        name: null,
      });
    } catch (e) {
      alert(`Failed: ${errorDetail(e) || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Poll the product while extraction is in progress.
  useEffect(() => {
    if (!pending?.id) return;
    if (pending.extraction_status !== "processing" && pending.extraction_status !== "pending") {
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${pending.id}`);
        if (cancelled) return;
        setPending((prev) =>
          prev && prev.id === data.id
            ? {
                ...prev,
                extraction_status: data.extraction_status,
                extraction_stage: data.extraction_stage,
                extraction_error: data.extraction_error,
                name: data.name,
              }
            : prev,
        );
        if (data.extraction_status === "completed") {
          // Navigate ONLY when everything is ready. The edit page will now load
          // with all the populated fields instead of a blank "Untitled product".
          setTimeout(() => nav(`/admin/products/${data.id}/edit`), 600);
        }
      } catch {
        // Ignore transient errors; keep polling.
      }
    };
    tick();
    const handle = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [pending?.id, pending?.extraction_status, nav]);

  // ============================================================
  // PROGRESS VIEW (shown after upload, replaces the dropzones)
  // ============================================================
  if (pending) {
    return (
      <AdminLayout>
        <ExtractionProgress
          pending={pending}
          onOpenAnyway={() => nav(`/admin/products/${pending.id}/edit`)}
          onRetry={async () => {
            setPending((p) => ({
              ...p,
              extraction_status: "processing",
              extraction_stage: "queued",
              extraction_error: null,
            }));
            try {
              await api.post(`/products/${pending.id}/extract`);
            } catch (e) {
              alert(errorDetail(e) || "Could not restart extraction");
            }
          }}
          onCancel={() => {
            // Lets the user upload another product. The current draft stays in DB
            // and is reachable from Products list.
            setPending(null);
            setDocs([]);
            setImages([]);
          }}
        />
      </AdminLayout>
    );
  }

  // ============================================================
  // UPLOAD VIEW (default)
  // ============================================================
  return (
    <AdminLayout>
      <div className="mb-6 md:mb-8">
        <div className="hed-overline">New product</div>
        <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
          Upload. Let AI do the typing.
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-heda-sub">
          Drop brochures, technical sheets, catalog pages and product images.
          Hedaply AI extracts a complete product profile you can edit before publishing.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Dropzone
          label="Documents"
          accept=".pdf,application/pdf"
          icon={FileText}
          help="PDF brochures, tech sheets, catalogs"
          items={docs}
          onDrop={(f) => uploadFiles(f, "document")}
          onRemove={(idx) => setDocs((x) => x.filter((_, i) => i !== idx))}
          uploading={uploading}
          uploadPct={uploadPct}
          testIdPrefix="doc"
        />
        <Dropzone
          label="Product Images"
          accept="image/*"
          icon={ImageIcon}
          help="JPG, PNG, WEBP — product photos"
          items={images}
          onDrop={(f) => uploadFiles(f, "image")}
          onRemove={(idx) => setImages((x) => x.filter((_, i) => i !== idx))}
          uploading={uploading}
          uploadPct={uploadPct}
          testIdPrefix="img"
          showPreview
        />
      </div>

      {/* AI extraction CTA */}
      <div className="heda-panel mt-6 flex flex-wrap items-center justify-between gap-4 p-5 md:p-7">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-heda-gold/30 bg-heda-gold/10 text-heda-gold">
            <Sparkles size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-[18px] font-bold text-heda-ink">Run AI extraction</div>
            <p className="mt-1 max-w-md text-[13px] text-heda-sub">
              Hedaply AI will read every page, identify the product, and structure all data.
              You&rsquo;ll see live progress here — no blank pages, no guessing.
            </p>
          </div>
        </div>
        <button
          data-testid="run-extraction-button"
          onClick={submit}
          disabled={creating || uploading}
          className="heda-btn-primary w-full sm:w-auto"
        >
          {creating && <Loader2 size={16} className="animate-spin" />}
          {creating ? "Creating draft..." : "Extract & Continue"}
          {!creating && <ArrowRight size={15} />}
        </button>
      </div>
    </AdminLayout>
  );
}

// ============================================================
// ExtractionProgress — Live status while AI runs in background.
// ============================================================
function ExtractionProgress({ pending, onOpenAnyway, onRetry, onCancel }) {
  const isFailed = pending.extraction_status === "failed";
  const currentStage = pending.extraction_stage || "queued";
  const currentIdx = STAGE_STEPS.findIndex((s) => s.key === currentStage);

  return (
    <div className="mx-auto max-w-2xl py-4 md:py-8">
      <div className="hed-overline">New product · AI extraction in progress</div>
      <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
        {isFailed
          ? "Something went wrong."
          : "Hedaply AI is reading your brochure."}
      </h1>
      <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-heda-sub">
        {isFailed
          ? "Don't worry — your draft is saved. You can retry, or open the draft and fill things manually."
          : "This usually takes 30–90 seconds depending on the file. You can leave this tab open — we&rsquo;ll take you to the finished product as soon as it&rsquo;s ready."}
      </p>

      <div
        className="heda-panel mt-6 p-5 md:p-7"
        data-testid="extraction-progress-card"
      >
        {/* Stage list */}
        <ul className="space-y-3">
          {STAGE_STEPS.map((step, idx) => {
            const done = !isFailed && currentIdx > idx;
            const active = !isFailed && currentIdx === idx;
            return (
              <li
                key={step.key}
                className="flex items-center gap-3"
                data-testid={`stage-${step.key}`}
              >
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {done ? (
                    <CheckCircle2
                      size={16}
                     
                      className="text-emerald-700"
                    />
                  ) : active ? (
                    <Loader2
                      size={14}
                     
                      className="animate-spin text-heda-gold"
                    />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-heda-line" />
                  )}
                </span>
                <span
                  className={`text-sm ${
                    done
                      ? "text-heda-mute line-through"
                      : active
                      ? "font-semibold text-heda-ink"
                      : "text-heda-mute"
                  }`}
                >
                  {step.label}
                  {active && "…"}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Failure banner */}
        {isFailed && (
          <div
            className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            data-testid="upload-extraction-error"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-1">AI extraction failed</div>
                <div className="tabular-nums text-[12px] leading-relaxed break-words">
                  {pending.extraction_error || "Unknown error"}
                  <TopUpLink text={pending.extraction_error} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          {isFailed ? (
            <>
              <button
                onClick={onRetry}
                data-testid="extraction-retry-button"
                className="heda-btn-primary"
              >
                <Sparkles size={15} /> Try again
              </button>
              <button
                onClick={onOpenAnyway}
                data-testid="extraction-open-anyway"
                className="hed-btn-line"
              >
                Open draft anyway
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onOpenAnyway}
                data-testid="extraction-open-anyway"
                className="hed-btn-line"
              >
                Open draft anyway
              </button>
              <button
                onClick={onCancel}
                data-testid="extraction-upload-another"
                className="px-2 py-2.5 text-sm text-heda-mute transition hover:text-heda-ink"
              >
                Upload another product
              </button>
            </>
          )}
        </div>
      </div>

      {pending.name && !isFailed && (
        <div className="mt-6 flex items-center gap-2 rounded-xl bg-heda-olive-tint px-4 py-3 text-sm text-heda-olive-dark">
          <Sparkles size={14} />
          Identified as <strong>{pending.name}</strong>
        </div>
      )}
    </div>
  );
}

function Dropzone({ label, accept, icon: Icon, help, items, onDrop, onRemove, uploading, uploadPct, testIdPrefix, showPreview }) {
  const [drag, setDrag] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDrag(false);
      onDrop(Array.from(e.dataTransfer.files));
    },
    [onDrop],
  );

  return (
    <div className="heda-panel p-5 md:p-7">
      <h2 className="mb-4 flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
        <Icon size={18} className="text-heda-gold" /> {label}
      </h2>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        className={`block cursor-pointer rounded-2xl border-2 border-dashed px-4 py-10 text-center transition ${
          drag ? "border-heda-gold bg-heda-gold-tint" : "border-heda-line bg-heda-cream/40 hover:border-heda-gold"
        }`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <input
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => onDrop(Array.from(e.target.files))}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <UploadCloud size={28} className="mx-auto mb-3 text-heda-mute" />
        <div className="text-sm font-semibold text-heda-ink">Drop files or click to upload</div>
        <div className="mt-1 text-[11.5px] text-heda-mute">{help}</div>
      </label>

      {uploading && (
        <div className="mt-3 flex items-center gap-2 text-xs text-heda-sub" data-testid={`${testIdPrefix}-upload-status`}>
          <Loader2 size={13} className="animate-spin text-heda-gold" />
          {uploadPct != null ? `Uploading… ${uploadPct}%` : "Uploading..."}
        </div>
      )}

      {items.length > 0 && (
        <div className={`mt-4 ${showPreview ? "grid grid-cols-3 gap-3" : "space-y-2"}`}>
          {items.map((it, idx) =>
            showPreview ? (
              <div key={it.id} className="relative group" data-testid={`${testIdPrefix}-preview-${idx}`}>
                <img
                  src={fileUrl(it.storage_path)}
                  alt={it.original_filename}
                  className="h-24 w-full rounded-xl border border-heda-line object-cover"
                />
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="absolute right-1 top-1 rounded-full border border-heda-line bg-white/90 p-1 transition sm:opacity-0 sm:group-hover:opacity-100"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div
                key={it.id}
                className="flex items-center justify-between rounded-xl border border-heda-line bg-heda-cream/50 px-3 py-2 text-sm"
                data-testid={`${testIdPrefix}-item-${idx}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <FileText size={14} className="shrink-0 text-heda-gold" />
                  <span className="truncate">{it.original_filename}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="ml-2 rounded-full p-1.5 text-heda-mute hover:bg-red-50 hover:text-red-700"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
